import { clamp } from '@deltasierra/utilities-math';
import { lighten } from '@material-ui/core';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { DSColour, getDSColour } from '../../theme/DSColours';

const Wrapper = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

interface ContentWrapperDivProps {
    $padding?: number;
}

const ContentWrapperDiv = styled.div<ContentWrapperDivProps>`
    position: absolute;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    padding: 0 ${({ $padding }) => $padding}px;
    max-width: 100%;

    flex-direction: column;
`;

interface StyledSvgBackgroundCircleProps {
    $strokeColour: string;
}

const StyledSvgBackgroundCircle = styled.circle<StyledSvgBackgroundCircleProps>`
    stroke: ${props => props.$strokeColour};
`;

interface StyledSvgDotCircleProps {
    $animationTime: number;
    $strokeColour: string;
}

const StyledSvgDotCircle = styled.circle<StyledSvgDotCircleProps>`
    stroke: ${props => props.$strokeColour};

    transition: stroke-dashoffset ${props => props.$animationTime}ms;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
`;

interface StyledSvgCircleProps {
    $animationTime: number;
    $strokeColour?: string;
}

const StyledSvgCircle = styled.circle<StyledSvgCircleProps>`
    stroke: ${props => props.$strokeColour ?? props.theme.palette.primary.main};

    transition: stroke-dashoffset ${props => props.$animationTime}ms;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
`;

type CircleProps = React.SVGProps<SVGCircleElement>;

function useColourEnumStroke(colour: DSColour | undefined): string {
    const theme = useTheme();
    switch (colour) {
        // Custom by default returns the primary color as the main
        case undefined:
            return getDSColour(DSColour.primary, theme);
        default:
            return getDSColour(colour, theme);
    }
}

function useColourEnumBackground(colour: DSColour | undefined, strokeColour: string): string {
    const theme = useTheme();
    switch (colour) {
        // For custom default to a neutral background
        case undefined:
            return theme.palette.grey[200];
        default:
            return lighten(strokeColour, 0.9);
    }
}

function useColourEnumDot(colour: DSColour | undefined, strokeColour: string): string {
    const theme = useTheme();
    switch (colour) {
        // Custom by default returns the contrast of the stroke colour
        case undefined:
            return theme.palette.getContrastText(strokeColour);
        default:
            return theme.palette.common.white;
    }
}

const DEFAULT_SIZE = 200;
const DEFAULT_STROKE_WIDTH = 15;

export function getNestedProgressCircleSize(
    childCount: number,
    baseSize = DEFAULT_SIZE,
    strokeWidth = DEFAULT_STROKE_WIDTH,
): number {
    return baseSize + strokeWidth * 2 * childCount;
}

export interface ProgressCircleProps {
    /**
     * The progress in percentage format 0 - 1 (0.5 = 50%)
     */
    progress: number;
    children?: React.ReactNode;

    size?: number;
    strokeWidth?: number;
    strokeCap?: CircleProps['strokeLinecap'];

    background?: boolean;
    dot?: boolean;

    /**
     * The base colour for this circle
     */
    colour?: DSColour;

    strokeColour?: CircleProps['stroke'];
    backgroundColour?: CircleProps['stroke'];
    dotColour?: CircleProps['stroke'];

    animateIn?: boolean;
    animationInDelay?: number;
    /**
     * The animation time for the transition in milliseconds
     */
    animationTime?: number;
}

export function ProgressCircle({
    animateIn = true,
    animationInDelay = 0,
    animationTime = 500,
    background = true,
    backgroundColour: _backgroundColour,
    children,
    colour,
    dot = true,
    dotColour: _dotColour,
    progress: initialProgress,
    size = DEFAULT_SIZE,
    strokeCap = 'round',
    strokeColour: _strokeColour,
    strokeWidth = DEFAULT_STROKE_WIDTH,
}: ProgressCircleProps): JSX.Element {
    const [deferredProgress, setProgress] = React.useState(animateIn ? 0 : initialProgress);

    const strokeColourFromEnum = useColourEnumStroke(colour);
    const strokeColour = _strokeColour ?? strokeColourFromEnum;

    const backgroundColourFromEnum = useColourEnumBackground(colour, strokeColour);
    const backgroundColour = _backgroundColour ?? backgroundColourFromEnum;

    const dotColourFromEnum = useColourEnumDot(colour, strokeColour);
    const dotColour = _dotColour ?? dotColourFromEnum;

    const normalizedRadius = size / 2 - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    React.useEffect(() => {
        if (animateIn) {
            setTimeout(() => {
                setProgress(initialProgress);
            }, Math.max(animationInDelay, 1));
        }
    }, [animateIn, animationInDelay, initialProgress]);

    const progress = clamp(0, 1, deferredProgress);
    const cPos = size / 2;

    return (
        <Wrapper>
            <svg height={size} width={size}>
                {background && (
                    <StyledSvgBackgroundCircle
                        $strokeColour={backgroundColour}
                        cx={cPos}
                        cy={cPos}
                        fill="transparent"
                        r={normalizedRadius}
                        strokeWidth={strokeWidth}
                    />
                )}
                <StyledSvgCircle
                    $animationTime={animationTime}
                    $strokeColour={strokeColour}
                    cx={cPos}
                    cy={cPos}
                    fill="transparent"
                    r={normalizedRadius}
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={circumference - progress * circumference}
                    strokeLinecap={strokeCap}
                    strokeWidth={strokeWidth}
                />
                {dot && (
                    <StyledSvgDotCircle
                        $animationTime={animationTime}
                        $strokeColour={dotColour}
                        cx={cPos}
                        cy={cPos}
                        fill="transparent"
                        r={normalizedRadius}
                        strokeDasharray={`0.5 ${circumference}`}
                        strokeDashoffset={circumference - (progress - 0.005) * circumference}
                        strokeLinecap="round"
                        strokeWidth={strokeWidth / 2}
                    />
                )}
            </svg>
            {children && <ContentWrapperDiv $padding={size / 4}>{children}</ContentWrapperDiv>}
        </Wrapper>
    );
}
ProgressCircle.displayName = 'ProgressCircle';
ProgressCircle.colours = DSColour;
ProgressCircle.getNestedSize = getNestedProgressCircleSize;
